import { buildGroupedExportView } from '../groupedExportView'
import {
  pnlSavedCriteria,
  balanceSheetSavedCriteria,
  savedPlCompare,
  isSavedCategoryPl,
} from '../savedRunCriteria'
import { buildPlStatementReport, netIncomeOf } from '@pages/reports/plStatement'
import { buildSegmentMatrix, hasPivotColumns } from '@pages/reports/segmentMatrix'
import {
  BALANCE_SHEET_BASE_HEADERS,
  buildBalanceSheetModel,
  toBalanceSheetGridReport,
} from '@pages/reports/balanceSheetModel'

/**
 * Spec 127 §4.6 (slice S7) — a STORED statement job, printed the way its page prints it.
 *
 * The queue drawer downloads a finished run from its stored result, with no page and
 * no grid mounted. Before this module a Balance Sheet job went through the generic
 * tabular adapter (`jobResultView.viewFromTabularResult`) and printed CB's raw export
 * rows — header echoes, the bare Liabilities / Equities side headings, the Section and
 * Parent Account bookkeeping columns, Amount typed from its strings — and a P&L job
 * (`{ report: { sections } }` is not a table) got no download at all.
 *
 * Now a statement job goes through the PAGE'S OWN pure builders — `buildPlStatementReport`
 * / `buildSegmentMatrix` for the P&L family, `buildBalanceSheetModel` +
 * `toBalanceSheetGridReport` for the Balance Sheet — and then through the grid's own pure
 * export-view builder, `buildGroupedExportView`, with the grid's DEFAULT state: fully
 * expanded, nothing rolled up, no filter, CB's columns. So the drawer's PDF body is the
 * page's default PDF body (pinned against the real page in
 * `ReportJobDrawer.statements.test.jsx`, I30). Nothing here computes a figure.
 *
 * Detected by PAYLOAD SHAPE, never by action key (the stored blob is what decides what
 * can be printed; an action key is a proxy for it):
 *   - P&L family   `report.sections` is an array
 *   - Balance Sheet the headers open with CB's four hard-coded export headers
 *                   (`Section, Account Number, Account, Parent Account`, ExportHelper.cs:3656)
 *                   — whatever follows, the page's model decides (a layout it cannot read
 *                   is the page's own flat fallback, exactly as the page shows it).
 *
 * ## What the P&L needs from the job, and why a missing value refuses
 *
 * The P&L page restores the run's COMPARE PERIOD and CHOOSE-REPORT MODE from the job's
 * `savedParams` when it reopens a saved result (`PLReport.jsx`, the `jid` branch), and
 * both change what the statement IS: the compare period adds the Prior / Change / %
 * columns and names the Prior column; the mode swaps the statement for the Account-Seg
 * or Category matrix. So a P&L job with no `savedParams` is REFUSED rather than printed
 * as a plain statement — a Comparative run silently printed without its comparison is a
 * plausible wrong document.
 *
 * A Category P&L is refused too: its criteria line must name the categories (CC-49973),
 * the drawer has no category list to name them from, and vladdemo has Category P&L gated
 * off, so there is no live capture to pin a build against. "Open report" still prints it.
 *
 * ## The criteria line
 *
 * `filtersSummary` is the page's own "Filters applied" line, rebuilt from the job's
 * `savedParams` with the same `reportFilterSummary` parts the pages use (the P&L's
 * `describePlCriteria`, the Balance Sheet's `filtersSummary` memo). It is `null` when a
 * part that changes every figure — the dates or the basis — cannot be read: the caller
 * then says the criteria were not recorded (the shell's wording, never "no filters").
 * One deliberate difference from the page: a Category Balance Sheet names its categories
 * from CB's own category COLUMNS (the CSV carries one per selected category), where the
 * page names them from its category list and says "All" when every one is picked.
 *
 * Spec 185 (CC-53435): both criteria builders now live in `../savedRunCriteria.js`, so
 * the REOPENED page states the same line for the same job instead of "not recorded".
 * The category difference above is settled there: a SAVED run (drawer and reopened page
 * alike) names categories from CB's columns; only a LIVE run says "All".
 *
 * Pure: no I/O, no React.
 */

export const STATEMENT_KIND = Object.freeze({ PNL: 'pnl', BALANCE_SHEET: 'balance-sheet' })

/** Why a statement job is not printed from the drawer. */
export const STATEMENT_REFUSAL = Object.freeze({
  NO_SAVED_PARAMS: 'no-saved-params',
  CATEGORY_PNL: 'category-pnl',
})

/**
 * The shell's wording for a saved run whose criteria cannot be recovered
 * (`SpecializedReportShell.jsx` SAVED_RUN_CRITERIA_UNKNOWN, CC-53363). Restated, not
 * imported: the shell module pulls the whole report chrome into the queue's bundle.
 * `statementJobView.test.js` pins the two equal.
 */
export const CRITERIA_NOT_RECORDED = 'not recorded for this saved run'

// The compare-period mapping (CB's `PLComparePeriods` integers → enum NAMES) and the
// stored-value readers moved to `../savedRunCriteria.js` (spec 185), where the reopened
// page reads them too. Sending or reading the NAME where CB wants the integer is silent
// (CC-49746), so the mapping is still pinned by the page-vs-drawer test.

/** The stored payload's body: the route's `{ data: … }` envelope, or the bare body. */
const bodyOf = (payload) => payload?.data ?? payload

/**
 * Which statement a stored payload is, by its shape — or null for anything else (the
 * caller keeps its tabular adapter for those).
 */
export const statementKindOf = (payload) => {
  const d = bodyOf(payload)
  if (Array.isArray(d?.report?.sections)) return STATEMENT_KIND.PNL
  const headers = Array.isArray(d?.headers) ? d.headers.map((h) => String(h ?? '').trim()) : []
  if (BALANCE_SHEET_BASE_HEADERS.every((h, i) => headers[i] === h)) return STATEMENT_KIND.BALANCE_SHEET
  return null
}

/** The grid's default export view of a statement grid report (expanded, unfiltered). */
const defaultStatementView = (grid) => buildGroupedExportView(grid, {
  // What `ReportGroupedGrid` hands the builder in statement mode with nothing rolled
  // up (`statement: statementMode ? { rolledIds, treeColumnId } : null`). Without it the
  // rows carry no tree kinds or depths, so "Total {parent}" lines would print unbolded
  // and sub-accounts flush left — not what the page prints.
  statement: { rolledIds: [], treeColumnId: grid?.treeColumnId != null ? String(grid.treeColumnId) : null },
})

function pnlJobView(d, savedParams) {
  if (!savedParams || typeof savedParams !== 'object') {
    return { kind: STATEMENT_KIND.PNL, refused: STATEMENT_REFUSAL.NO_SAVED_PARAMS }
  }
  const sp = savedParams
  // The page's reopen rules, verbatim (PLReport.jsx): categories win, then a segment.
  if (isSavedCategoryPl(sp)) {
    return { kind: STATEMENT_KIND.PNL, refused: STATEMENT_REFUSAL.CATEGORY_PNL }
  }
  const report = d.report
  const ranCompare = savedPlCompare(sp)
  // The plain P&L page always sends 0, so this is the page's
  // `isComparison && ranCompare !== 'None'` for every job either page can create.
  const comparisonActive = ranCompare !== 'None'
  const matrix = Number(sp.accountSegValueId) > 0 && hasPivotColumns(report)
  const grid = matrix
    ? buildSegmentMatrix({ report, sections: report?.sections || [], netIncome: netIncomeOf(report), mode: 'segment' })
    : buildPlStatementReport(report, { comparisonActive, ranCompare })

  // PLReport.jsx describePlCriteria(filters, categories, window): dates, basis, and —
  // on a comparison run — the window CB reported for it. A Category run never gets here.
  // Spec 185: the one builder the reopened page uses as well.
  const filtersSummary = pnlSavedCriteria(sp, report)
  return { kind: STATEMENT_KIND.PNL, view: defaultStatementView(grid), filtersSummary }
}

function balanceSheetJobView(d, savedParams) {
  // `savedResult: true`: a stored result with no `hierarchy` was produced before
  // roll-up existed (every Balance Sheet job stored before spec 127 S1 deployed —
  // measured: the deployed QA worker's job result carries none). The model reads that
  // as roll-up UNAVAILABLE, never as an error, and still builds CB's sections; the
  // default export is fully expanded, so nothing on paper depends on roll-up anyway.
  const model = buildBalanceSheetModel(d, { savedResult: true })
  const grid = toBalanceSheetGridReport(model, { layout: 'single' })

  // BalanceSheetReport.jsx's filtersSummary: as of, basis, zero rows, categories. The
  // side-by-side part is never added: the drawer prints the single-column statement.
  // Spec 185: the one builder the reopened page uses as well.
  const filtersSummary = balanceSheetSavedCriteria(savedParams, model.headers || [])
  return { kind: STATEMENT_KIND.BALANCE_SHEET, view: defaultStatementView(grid), filtersSummary }
}

/**
 * @param {object} payload     the stored job result (`GET /reports/jobs/:id/result`)
 * @param {object} [opts]
 * @param {object} [opts.savedParams] the job's `savedParams` (`GET /reports/jobs/:id`)
 * @returns {null | { kind, refused } | { kind, view, filtersSummary: string|null }}
 *   null when the payload is not a statement (the caller's tabular adapter applies);
 *   `refused` when it is one the drawer will not print; otherwise the page's default
 *   export view and its criteria line.
 */
export function statementJobView(payload, { savedParams } = {}) {
  const kind = statementKindOf(payload)
  if (!kind) return null
  const d = bodyOf(payload)
  return kind === STATEMENT_KIND.PNL ? pnlJobView(d, savedParams) : balanceSheetJobView(d, savedParams)
}

/**
 * A GROUPED generic result (CB's report object with group rows — what the page's
 * `ReportView` hands `ReportGroupedGrid`) as the view its page's grid publishes by
 * default: CB's visible columns, expanded, unfiltered, with the `outline` that gives
 * the PDF its grouped body (headings, rows, CB's subtotal lines). Null for a flat
 * result, which keeps `viewFromJobResult` (review of spec 127: the drawer printed
 * every grouped generic job as a flat leaf dump, hidden metadata columns included,
 * while the page printed it grouped).
 */
export const groupedResultView = (report) => (
  Array.isArray(report?.reportData) && report.reportData.some((r) => r?.isGroup)
    ? buildGroupedExportView(report)
    : null
)

export default statementJobView
